import { ShopOutlined } from '@ant-design/icons';
import {
  Empty,
  Input,
  Radio,
  Spin,
  Table,
  Tag,
  type RadioChangeEvent,
  type TableProps,
} from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageTitle } from '@client/components/PageTitle';
import { EAbilitySubjects } from '@client/hooks/useAbilities';
import { PageLayout } from '@client/layouts/page.layout';
import { FeatureFeatureToggle } from '@client/pages/admin/marketplace/feature-feature-toggle';
import { useMarketplaceQueries } from '@client/queries/useMarketplaceQueries';

import styles from './marketplace.module.less';

import type { IFeatureMarketplaceListItem } from '@common/types';
import type { ColumnsType } from 'antd/es/table';

type TFeaturedFilter = 'all' | 'featured';

const PAGE_SIZE = 50;

/**
 * Step 5: admin marketplace curation page for individual user features.
 * Mirrors `AdminMarketplacePage` shape — same name search, featured filter,
 * per-row featured switch — pointed at the features list/admin endpoint.
 * @returns {React.JSX.Element} The rendered page
 */
export const AdminMarketplaceFeaturesPage: React.FC = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { useMarketplaceFeaturesList } = useMarketplaceQueries();

  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<TFeaturedFilter>('all');

  const queryParams = useMemo(
    () => ({
      limit: PAGE_SIZE,
      ...(filter === 'featured' ? { featured: true } : {}),
      ...(search ? { q: search } : {}),
    }),
    [filter, search],
  );

  const { data, isLoading, isFetching } =
    useMarketplaceFeaturesList(queryParams);

  const items: IFeatureMarketplaceListItem[] = data?.items ?? [];

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchInput(event.target.value);
  };

  const onSearchSubmit = (value: string): void => {
    setSearch(value.trim());
  };

  const onFilterChange = (event: RadioChangeEvent): void => {
    setFilter(event.target.value as TFeaturedFilter);
  };

  const columns: ColumnsType<IFeatureMarketplaceListItem> = [
    {
      title: t('admin.marketplace.features.column.title'),
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: t('admin.marketplace.column.owner'),
      dataIndex: 'ownerHandle',
      key: 'ownerHandle',
      render: (handle: string) => (handle ? `@${handle}` : '-'),
    },
    {
      title: t('admin.marketplace.features.column.price'),
      key: 'price',
      align: 'right',
      render: (_, record: IFeatureMarketplaceListItem) =>
        `${(record.currentPrice ?? 0).toFixed(2)} ${record.currency ?? 'USD'}`,
    },
    {
      title: t('admin.marketplace.column.clones'),
      dataIndex: 'cloneCount',
      key: 'cloneCount',
      align: 'right',
    },
    {
      title: t('admin.marketplace.column.public'),
      key: 'public',
      align: 'center',
      render: () => <Tag color="green">{t('admin.marketplace.publicYes')}</Tag>,
    },
    {
      title: t('admin.marketplace.column.featured'),
      key: 'featured',
      align: 'center',
      render: (_, record: IFeatureMarketplaceListItem) => (
        <FeatureFeatureToggle id={record.id} featured={record.featured} />
      ),
    },
  ];

  const tableProps: TableProps<IFeatureMarketplaceListItem> = {
    columns,
    dataSource: items,
    rowKey: (record) => record.id,
    loading: isLoading || isFetching,
    pagination: {
      pageSize: PAGE_SIZE,
      showSizeChanger: false,
    },
    bordered: true,
    size: 'small',
    locale: {
      emptyText: (
        <div className={styles.empty}>
          <Empty
            description={t('admin.marketplace.features.empty')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ),
    },
    title: () => (
      <div className={styles.filters}>
        <Input.Search
          allowClear
          placeholder={t('admin.marketplace.search')}
          value={searchInput}
          onChange={onSearchChange}
          onSearch={onSearchSubmit}
          style={{ maxWidth: 320 }}
        />
        <Radio.Group value={filter} onChange={onFilterChange}>
          <Radio.Button value="all">
            {t('admin.marketplace.filters.all')}
          </Radio.Button>
          <Radio.Button value="featured">
            {t('admin.marketplace.filters.featured')}
          </Radio.Button>
        </Radio.Group>
        <span className={styles.spacer} />
      </div>
    ),
  };

  return (
    <PageLayout
      subject={EAbilitySubjects.MARKETPLACE}
      title={
        <PageTitle
          Icon={ShopOutlined}
          name={t('admin.marketplace.features.title')}
        />
      }
    >
      {isLoading && !data ? (
        <Spin spinning />
      ) : (
        <Table<IFeatureMarketplaceListItem> {...tableProps} />
      )}
    </PageLayout>
  );
};

export default AdminMarketplaceFeaturesPage;
